/* The sign-in guard every app screen sits behind, in the web app's shell and
 * in the field app alike: the screen it shows in place of its children comes
 * from lib/sessionGate.ts.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { account, activeDirectory, ready, signOut } from "../auth";
import { useChooseTenant, useDropLapsedChoice, useMe } from "../api/me";
import { useStepStatus } from "../api/secondFactor";
import { Button } from "../components/Button";
import { Logo } from "../components/Logo";
import { Notice } from "../components/Notice";
import { useHasNetwork } from "../lib/network";
import { gateView, localNotice } from "../lib/sessionGate";
import { tenantLabel } from "../lib/tenantChoice";
import { SecondFactorStep } from "../routes/SecondFactorStep";
import s from "./SessionGate.module.css";

/* `local` is for an app that keeps its work on the device: it is shown without
 * a session, under a notice saying why nothing syncs, where any other app is
 * sent to sign-in (lib/sessionGate.ts). */
export function SessionGate({ children, local = false }: { children: ReactNode; local?: boolean }) {
  const navigate = useNavigate();
  // Null until MSAL has settled, then whether it holds an account.
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const { data: me } = useMe(signedIn === true);
  const chooseTenant = useChooseTenant();
  useDropLapsedChoice();
  const external = signedIn === true && activeDirectory() === "external";
  const step = useStepStatus(external);
  const network = useHasNetwork();

  // The guard: a visitor with no active Entra session is sent to sign-in
  // rather than seeing a shell that renders mock data as if it belonged to
  // them. Screens no route serves read mocks/corpus.ts, but reaching them at
  // all requires a real session.
  useEffect(() => {
    let cancelled = false;
    ready()
      .then(() => {
        if (!cancelled) setSignedIn(account() !== null);
      })
      .catch(() => {
        if (!cancelled) setSignedIn(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const view = gateView({
    checking: signedIn === null,
    signedIn: signedIn === true,
    local,
    external,
    step: {
      pending: step.isPending,
      failed: step.error !== null,
      // fetch rejects with a TypeError only when no response arrived at all;
      // a refusal is an answer, and fetchJson raises it as an Error.
      unreachable: step.error instanceof TypeError,
      status: step.data,
    },
    me,
  });

  useEffect(() => {
    if (view === "signIn") navigate("/", { replace: true });
  }, [view, navigate]);

  if (view === "wait" || view === "signIn") return null;

  // The app on what the device holds, with the reason nothing syncs: no
  // account, or an account the API cannot be reached to check.
  if (view === "local") {
    const notice = localNotice(signedIn === true, network);
    return (
      <>
        <div className={s["session-gate__local"]}>
          {notice === "unreachable" && (
            <Notice>No connection. Work you save stays on this phone until it can sync.</Notice>
          )}
          {notice === "signedOut" && (
            <>
              <Notice>Signed out. Work you save stays on this phone, and syncing it needs a sign-in.</Notice>
              <button className="btn btn--tertiary" type="button" onClick={() => navigate("/")}>
                Sign in
              </button>
            </>
          )}
          {notice === "signedOutOffline" && (
            <Notice>Signed out. Work you save stays on this phone; sign in to sync it once there's a signal.</Notice>
          )}
        </div>
        {children}
      </>
    );
  }

  // The second-factor step's status could not be read: the refusal, and a way out.
  if (view === "stepError") {
    return (
      <div className={s["session-gate__panel"]}>
        <Notice tone="error">{step.error?.message}</Notice>
        <button className="btn btn--tertiary" type="button" onClick={signOut}>
          Sign out
        </button>
      </div>
    );
  }

  // The second-factor step, for an External ID sign-in that has not passed it
  // this session. Shown in place of the shell, so no screen mounts and fetches
  // first; the API refuses their data either way (app/second_factor.py).
  if (view === "step" && step.data) return <SecondFactorStep status={step.data} />;

  // The choice of tenant, for somebody offered more than one with none
  // chosen in this tab. Shown in place of the shell for the step's reason: the
  // API refuses every request that names no tenant (15 section 2.2), so a
  // screen mounted first would only fetch refusals.
  if (view === "choose" && me) {
    return (
      <div className={s["session-gate__panel"]}>
        <Logo height={24} />
        <h1 className="page__title">Choose a tenant</h1>
        {chooseTenant.error && <Notice tone="error">{chooseTenant.error.message}</Notice>}
        {me.tenants.map((t) => (
          <Button key={t.id} disabled={chooseTenant.isPending} onClick={() => chooseTenant.mutate(t.id)}>
            {tenantLabel(t)}
          </Button>
        ))}
        <button className="btn btn--tertiary" type="button" onClick={signOut}>
          Sign out
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
